<template>
    <v-card class="mt-3">
        <v-toolbar>
            <v-toolbar-title> Fittinguri </v-toolbar-title>
            <v-toolbar-items>
                <v-btn @click="addItem" size="x-large">
                    <v-icon color="success"> mdi-plus </v-icon>
                </v-btn>
            </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
            <v-row>
                <v-col
                    v-for="(item, index) in plumbingItems"
                    :key="item.id"
                    cols="12"
                >
                    <PlumbingSetupItem
                        ref="items"
                        :plumbing-item="item"
                        :plumbing-fittings="formattedAvailablePlumbingFittings"
                        @update-item="updateItem(index, $event)"
                        @remove-item="removeItem(index)"
                        @add-row="handleAddRow(index)"
                    />
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>
</template>

<script>
import PlumbingSetupItem from "@/components/offer/PlumbingSetupItem.vue";
import apiClient from "@/utils/apiClient";
import { v4 as uuidv4 } from "uuid";

export default {
    name: "PlumbingSetup",
    components: { PlumbingSetupItem },
    data() {
        return {
            plumbingItems: [{ id: uuidv4(), fitting: null, quantity: 1 }],
        };
    },
    emits: ["update-selected-plumbing-items", "trigger-recalculation"],
    props: {
        plumbingFittingProducts: {
            type: Array,
            required: true,
        },
        offerId: {
            type: String,
        },
    },
    computed: {
        /**
         * Formats available plumbing fittings by mapping product names to codes for v-select options.
         * @returns {Array} Array of objects with `title` and `value` for each fitting.
         */
        formattedAvailablePlumbingFittings() {
            return this.plumbingFittingProducts.map((product) => ({
                title: product.product_name,
                value: product.product_code,
            }));
        },
    },
    async mounted() {
        // Check if there's an offerId and fetch the plumbing setup if it exists
        if (this.offerId) {
            await this.fetchPlumbingSetup(this.offerId);
        }
    },
    methods: {
        /**
         * Fetches the plumbing setup for a given offer ID and populates the plumbingItems array.
         * @param {String} offerId - The ID of the offer to fetch the plumbing setup for.
         * @returns {Promise<void>}
         */
        async fetchPlumbingSetup(offerId) {
            try {
                const response = await apiClient.get(
                    `/api/plumbing-setup/${offerId}/`
                );
                const plumbingSetup = response.data.plumbing_items;

                // Set the selected plumbing items based on the fetched setup
                this.plumbingItems = plumbingSetup.map((item) => ({
                    fitting: item.product_code,
                    quantity: item.quantity,
                }));
            } catch (error) {
                console.error("Error fetching plumbing setup:", error);
            }
        },
        /**
         * Adds a new plumbing item to the plumbingItems array with default values.
         */
        addItem() {
            this.plumbingItems.push({
                id: uuidv4(),
                fitting: null,
                quantity: 1,
            });
            this.$emit("trigger-recalculation");
        },
        /**
         * Removes a plumbing item from the plumbingItems array at the given index.
         * @param {Number} index - The index of the plumbing item to remove.
         */
        removeItem(index) {
            this.plumbingItems.splice(index, 1);
            this.$emit("trigger-recalculation");
        },
        /**
         * Updates a plumbing item in the plumbingItems array at the given index with new data.
         * @param {Number} index - The index of the plumbing item to update.
         * @param {Object} updatedItem - The updated plumbing item object with fitting and quantity.
         */
        updateItem(index, updatedItem) {
            // Mutate the existing item instead of replacing it
            this.plumbingItems[index].fitting = updatedItem.fitting;
            this.plumbingItems[index].quantity = updatedItem.quantity;
            this.$emit("trigger-recalculation");
        },
        /**
         * Adds a new row or shifts focus to the next row when the user presses
         * Enter or Tab in the "Quantity" field.
         *
         * - If the current index is the last row, a new row will be added and
         *   the focus will be set to the "Model" field of the newly added row.
         * - If the current index is not the last row, the focus will move to
         *   the "Model" field of the next existing row.
         *
         * @param {number} index - The index of the current row in the `plumbingItems` array.
         * @returns {void}
         */
        handleAddRow(index) {
            if (index === this.plumbingItems.length - 1) {
                this.addItem();

                this.$nextTick(() => {
                    // focus the model of the last row (newly added)
                    const lastChild =
                        this.$refs.items[this.$refs.items.length - 1];
                    lastChild?.focusModel();
                });
            } else {
                // focus the model of the next row if it already exists
                this.$nextTick(() => {
                    const nextChild = this.$refs.items[index + 1];
                    nextChild?.focusModel();
                });
            }
        },
    },
    watch: {
        /**
         * Watches for changes in plumbingItems and emits an event to update the parent component.
         * If any item has a falsy property, it is removed from the plumbingItems array.
         * @param {Array} newItems - The updated plumbing items array.
         */
        plumbingItems: {
            handler: function (newItems) {
                this.$emit("update-selected-plumbing-items", newItems);
            },
            deep: true,
        },
    },
};
</script>

<style scoped></style>
